import { useEffect, useState, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom';
import DOMPurify from 'dompurify';
import { PageSkeleton } from '@/components/PageSkeleton';
import { DashboardSkeleton, DeckFeedSkeleton } from '@/components/skeletons';
import { AppLayout } from '@/components/layout/AppLayout';
import { InfiniChatWidget } from '@/components/chat/InfiniChatWidget';
const EmailValidationModal = lazy(() =>
  import('@/components/EmailValidationModal').then(m => ({ default: m.EmailValidationModal }))
);
import { LoadingScreen } from '@/components/LoadingScreen';
import { useAuthStore } from '@/stores/authStore';
import { useEnergyStore } from '@/stores/energyStore';
import { useSessionHeartbeat } from '@/hooks/useSessionHeartbeat';
import { usePageTracking } from '@/hooks/usePageTracking';
import { MBToastContainer } from '@/components/feedback';
import { LevelUpOverlay } from '@/components/LevelUpOverlay';


// ================================================================
// LAZY LOADED PAGES — Code splitting per route
// Each page loads only when the user navigates to it.
// Named exports are wrapped with .then(m => ({ default: m.X }))
// ================================================================

// Public
const LoginPage = lazy(() => import('@/pages/login-page').then(m => ({ default: m.LoginPage })));
const AppPage = lazy(() => import('@/pages/app-page').then(m => ({ default: m.AppPage })));
const InviteRedeemPage = lazy(() => import('@/pages/InviteRedeemPage').then(m => ({ default: m.InviteRedeemPage })));
const ConvitePage = lazy(() => import('@/pages/convite').then(m => ({ default: m.ConvitePage })));
const DoseAutenticidadePage = lazy(() => import('@/pages/missions/dose-autenticidade').then(m => ({ default: m.DoseAutenticidadePage })));
const FichaTecnicaPage = lazy(() => import('@/pages/ficha-tecnica-page').then(m => ({ default: m.FichaTecnicaPage })));
const DNAImersivoPublicPage = lazy(() => import('@/features/diario-bordo/DNAImersivoPublicPage').then(m => ({ default: m.DNAImersivoPublicPage })));

// Onboarding (builder pivot)
const OnboardingPage = lazy(() => import('@/pages/onboarding-page').then(m => ({ default: m.OnboardingPage })));

// Protected — Core
const DashboardPage = lazy(() => import('@/pages/madboat-dashboard-page').then(m => ({ default: m.DashboardPage })));
const OdisseiaPage = lazy(() => import('@/pages/odisseia-page').then(m => ({ default: m.OdisseiaPage })));
const JornadasPage = lazy(() => import('@/pages/jornadas-page').then(m => ({ default: m.JornadasPage })));
const VibeCodingJourneyPage = lazy(() => import('@/pages/journeys/vibecoding/vibecoding-journey-page').then(m => ({ default: m.VibeCodingJourneyPage })));
// Protected — Account
const SettingsPage = lazy(() => import('@/pages/SettingsPage').then(m => ({ default: m.SettingsPage })));
const AccountPage = lazy(() => import('@/pages/AccountPage').then(m => ({ default: m.AccountPage })));
const StorePage = lazy(() => import('@/pages/StorePage').then(m => ({ default: m.StorePage })));
const ConvitesPage = lazy(() => import('@/pages/ConvitesPage').then(m => ({ default: m.ConvitesPage })));

// Protected — Diario de Bordo (each loaded independently, NOT via barrel)
const DiarioBordoPage = lazy(() => import('@/features/diario-bordo/DiarioBordoPage').then(m => ({ default: m.DiarioBordoPage })));
const TimelinePage = lazy(() => import('@/features/diario-bordo/TimelinePage').then(m => ({ default: m.TimelinePage })));
const DNACriativoPage = lazy(() => import('@/features/diario-bordo/DNACriativoPage').then(m => ({ default: m.DNACriativoPage })));
const DNAComportamentalPage = lazy(() => import('@/features/diario-bordo/DNAComportamentalPage').then(m => ({ default: m.DNAComportamentalPage })));
const BussolaMercadoPage = lazy(() => import('@/features/diario-bordo/BussolaMercadoPage'));
const DNAImersivoPage = lazy(() => import('@/features/diario-bordo/DNAImersivoPage').then(m => ({ default: m.DNAImersivoPage })));
const MissaoEstrategicaPage = lazy(() => import('@/features/diario-bordo/MissaoEstrategicaPage').then(m => ({ default: m.MissaoEstrategicaPage })));
const DNAImersivo = lazy(() => import('@/features/diario-bordo/components').then(m => ({ default: m.DNAImersivo })));
const SkillTreePage = lazy(() => import('@/features/diario-bordo/components/skill-tree/SkillTreePage').then(m => ({ default: m.SkillTreePage })));
const PlanoFugaPage = lazy(() => import('@/features/diario-bordo/components/plano-fuga/PlanoFugaPage').then(m => ({ default: m.PlanoFugaPage })));

// Protected — Deck
const DeckFeedPage = lazy(() => import('@/pages/deck/DeckFeedPage').then(m => ({ default: m.DeckFeedPage })));
const DeckProfilePage = lazy(() => import('@/pages/deck/DeckProfilePage').then(m => ({ default: m.DeckProfilePage })));
const DeckShowcasePage = lazy(() => import('@/pages/deck/DeckShowcasePage').then(m => ({ default: m.DeckShowcasePage })));

// Protected — Vortex
const VortexPage = lazy(() => import('@/pages/vortex/VortexPage').then(m => ({ default: m.VortexPage })));

// Protected — MadTimes
const MadTimesPage = lazy(() => import('@/pages/MadTimesPage').then(m => ({ default: m.MadTimesPage })));

/**
 * Static Page Component
 * Loads and displays static HTML files inline
 */
function StaticPage({ url }: { url: string }) {
  const [html, setHtml] = useState('');

  useEffect(() => {
    fetch(url)
      .then(res => res.text())
      .then(setHtml)
      .catch(() => setHtml('<p>Failed to load page</p>'));
  }, [url]);

  return <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(html) }} />;
}

/**
 * Main App Component
 * Handles routing and protected routes
 */
function App() {
  const [showLoading, setShowLoading] = useState(true);

  // Skip loading screen for convite page (has its own loading)
  const isConvitePage = window.location.pathname === '/convite' || window.location.pathname.startsWith('/invite');

  // Show loading screen on first load (except for convite)
  if (showLoading && !isConvitePage) {
    return <LoadingScreen onComplete={() => setShowLoading(false)} />;
  }

  return (
    <BrowserRouter>
      <AppContent />
    </BrowserRouter>
  );
}

/**
 * App Content - Inside BrowserRouter to access useLocation
 */
function AppContent() {
  // Setup session heartbeat (keeps session alive)
  useSessionHeartbeat();
  // Rastreia mudanças de página no Posthog
  usePageTracking();

  const location = useLocation();
  const { token, user, refreshUser } = useAuthStore();
  const [showEmailValidation, setShowEmailValidation] = useState(false);

  const { fetchStatus: fetchEnergy, fetchConfig: fetchEnergyConfig } = useEnergyStore();

  // Refresh user data + energy on app load
  useEffect(() => {
    if (token && user) {
      refreshUser();
      fetchEnergy();
      fetchEnergyConfig();
    }
  }, []);

  // Check if user needs onboarding (new builder flow)
  // Only redirect if explicitly false (not undefined/null — existing users may not have the field)
  const needsOnboarding = !!(token && user && (user as any).onboarding_completed === false);

  // Public routes where modal should NOT appear
  const isPublicRoute = location.pathname === '/login' ||
                        location.pathname === '/app' ||
                        location.pathname === '/convite' ||
                        location.pathname.startsWith('/invite') ||
                        location.pathname.startsWith('/m/') ||
                        location.pathname.startsWith('/missao/') ||
                        location.pathname.startsWith('/ficha/') ||
                        location.pathname.startsWith('/dna-imersivo/') ||
                        location.pathname === '/terms' ||
                        location.pathname === '/privacy';

  // Immersive routes where InfiniChat should be hidden for focus
  const isImmersiveRoute = location.pathname === '/onboarding' ||
                           location.pathname === '/diario' ||
                           location.pathname.startsWith('/diario/') ||
                           location.pathname === '/diario/dna/cinema';

  // Check if viewing MADTIMES section (minimize chat, don't hide it)
  const isMadTimesView = location.pathname === '/madtimes' || location.hash === '#madtimes' || location.search.includes('view=madtimes');

  // Check if viewing PRICING section (hide chat and navbar completely)
  const isPricingView = location.search.includes('view=pricing');

  // Email validation is now ON-DEMAND only (triggered from settings/account page)
  // Users can use the app without verifying email first
  // Verification is required only for: password reset, email change, sensitive actions

  // Handle email validation complete
  const handleEmailValidationComplete = () => {
    setShowEmailValidation(false);
    // User data is already updated by EmailValidationModal via setAuth
  };

  return (
    <>
      {/* Global Toast Notifications */}
      <MBToastContainer />

      {/* Global Level-Up Celebration */}
      <LevelUpOverlay />

      {/* Email Validation Modal - Now triggered on-demand from settings/account */}
      {showEmailValidation && (
        <Suspense fallback={null}>
          <EmailValidationModal onComplete={handleEmailValidationComplete} />
        </Suspense>
      )}

      {/* InfiniChat Widget - Only show when authenticated and not on immersive routes */}
      {/* Minimized when viewing MADTIMES section, hidden when viewing PRICING */}
      {token && user && !isPublicRoute && !isImmersiveRoute && !isPricingView && (
        <InfiniChatWidget forceMinimized={isMadTimesView} />
      )}

      <Suspense fallback={<div style={{ position: 'fixed', inset: 0, background: '#000' }} />}>
        <Routes>
          {/* ===== Public Routes ===== */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/app" element={<AppPage />} />
          <Route path="/invite/:code" element={<InviteRedeemPage />} />
          <Route path="/invite" element={<InviteRedeemPage />} />
          <Route path="/terms" element={<StaticPage url="/terms.html" />} />
          <Route path="/privacy" element={<StaticPage url="/privacy.html" />} />
          <Route path="/convite" element={<ConvitePage />} />
          <Route path="/m/dose" element={<DoseAutenticidadePage />} />
          <Route path="/missao/dose" element={<DoseAutenticidadePage />} />
          <Route path="/missao/dose-autenticidade" element={<DoseAutenticidadePage />} />
          <Route path="/dna-imersivo/:slug" element={<DNAImersivoPublicPage />} />
          <Route path="/ficha/:slug" element={<FichaTecnicaPage />} />

          {/* ===== Onboarding (fullscreen, no AppLayout) ===== */}
          <Route element={<ProtectedRoute />}>
            <Route path="/onboarding" element={<OnboardingPage />} />
          </Route>

          {/* ===== Protected Routes with AppLayout (shared nav bar) ===== */}
          <Route element={<ProtectedRoute />}>
            <Route element={<AppLayout />}>
              {/* Core */}
              <Route path="/dashboard" element={<DashboardRoute />} />
              <Route path="/odisseia" element={<OdisseiaPage />} />
              <Route path="/jornadas" element={<Navigate to="/odisseia" state={{ tab: 'mapa' }} replace />} />
              <Route path="/jornada/vibecoding" element={<VibeCodingJourneyPage />} />
              <Route path="/conquistas" element={<Navigate to="/odisseia" state={{ tab: 'conquistas' }} replace />} />

              {/* Account */}
              <Route path="/settings" element={<SettingsPage />} />
              <Route path="/account" element={<AccountPage />} />
              <Route path="/store" element={<StorePage />} />
              <Route path="/convites" element={<ConvitesPage />} />
              <Route path="/minha-ficha" element={<FichaTecnicaPage />} />

              {/* Diario de Bordo */}
              <Route path="/diario" element={<DiarioBordoPage />} />
              <Route path="/diario/timeline" element={<TimelinePage />} />
              <Route path="/diario/dna" element={<DNACriativoPage />} />
              <Route path="/diario/bussola" element={<BussolaMercadoPage />} />
              <Route path="/diario/dna-imersivo" element={<DNAImersivoPage />} />
              <Route path="/diario/dna/imersivo" element={<DNAImersivo onBack={() => window.history.back()} />} />
              <Route path="/diario/dna/:id" element={<DNACriativoPage />} />
              <Route path="/diario/dna-comportamental" element={<DNAComportamentalPage />} />
              <Route path="/diario/missao-estrategica" element={<MissaoEstrategicaPage />} />
              <Route path="/diario/skill-tree" element={<SkillTreePage />} />
              <Route path="/diario/plano-de-fuga" element={<PlanoFugaPage />} />

              {/* Deck */}
              <Route path="/deck" element={<Suspense fallback={<DeckFeedSkeleton />}><DeckShowcasePage /></Suspense>} />
              <Route path="/deck/tripulantes" element={<Suspense fallback={<DeckFeedSkeleton />}><DeckFeedPage /></Suspense>} />
              <Route path="/u/:username" element={<DeckProfilePage />} />

              {/* Vortex */}
              <Route path="/vortex" element={<Suspense fallback={<div style={{ width: '100%', minHeight: '100dvh', background: '#08060d' }} />}><VortexPage /></Suspense>} />

              {/* MadTimes */}
              <Route path="/madtimes" element={<Suspense fallback={<div style={{ width: '100%', minHeight: '100dvh', background: '#0A0A0F' }} />}><MadTimesPage /></Suspense>} />
              <Route path="/madtimes/:slug" element={<Suspense fallback={<div style={{ width: '100%', minHeight: '100dvh', background: '#0A0A0F' }} />}><MadTimesPage /></Suspense>} />
            </Route>
          </Route>

          {/* Default & 404 */}
          <Route path="/" element={<Navigate to="/login" replace />} />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </Suspense>
    </>
  );
}

/**
 * Protected Route Wrapper (Layout version)
 * Uses Outlet for nested route rendering.
 * Checks both Zustand store and localStorage directly.
 */
function ProtectedRoute() {
  const { token } = useAuthStore();

  // Also check localStorage directly as fallback (in case Zustand hasn't hydrated yet)
  const getStoredToken = () => {
    try {
      const stored = localStorage.getItem('madboat-auth');
      if (stored) {
        const parsed = JSON.parse(stored);
        return parsed?.state?.token || null;
      }
    } catch {
      // Ignore errors
    }
    return null;
  };

  const storedToken = getStoredToken();
  const hasAuth = !!(token || storedToken);

  if (hasAuth) {
    return <Outlet />;
  }

  return <Navigate to="/login" replace />;
}

/**
 * Dashboard Route — wrapped in Suspense to avoid inline ternary issues
 */
function DashboardRoute() {
  return (
    <Suspense fallback={<DashboardSkeleton />}>
      <DashboardPage />
    </Suspense>
  );
}

export default App;
